import { logger } from './logger'
import { getFunnelIdFromPath } from './funnelUtils'
import { sessionStore } from './sessionKey'

/**
 * Promo Manager Utility
 * Handles fetching and storing promo code data from the backend.
 *
 * Funnel-aware: each request includes the active funnel id (e.g.
 * variant-cv-1, variant-cv-nad). The session cache is keyed by
 * `${affid}::${funnelId}` so navigating between funnels in the same tab
 * triggers a refetch with the correct ACF field group.
 */

// Global flag to prevent multiple simultaneous promo fetches
// This is shared across all calls to prevent duplicate requests
let globalPromoFetchInProgress = false
let globalPromoFetched = false

const buildCacheKey = (affid, funnelId) => `${affid || ''}::${funnelId || ''}`

/**
 * Check if promo data is already stored in sessionStorage
 * @returns {boolean} True if promo data exists in session
 */
export const hasPromoDataInSession = () => {
  const promoRibbon = sessionStore.getItem('promo_ribbon');
  const cvPromoRibbon = sessionStore.getItem('cv_promo_ribbon');
  const promoId = sessionStore.getItem('promo_id');
  const cvPromoId = sessionStore.getItem('cv_promo_id');
  const percentOff = sessionStore.getItem('percent_off');
  const cvPercentOff = sessionStore.getItem('cv_percent_off');
  const amountOff = sessionStore.getItem('amount_off');
  const cvAmountOff = sessionStore.getItem('cv_amount_off');

  // Check if all required fields exist
  return !!(promoRibbon || promoId || percentOff || amountOff || cvPromoRibbon || cvPromoId || cvPercentOff || cvAmountOff);
};

/**
 * Get promo data from sessionStorage
 * @returns {Object|null} Promo data object or null if not found
 */
export const getPromoDataFromSession = () => {
  if (!hasPromoDataInSession()) {
    return null;
  }

  return {
    promo_ribbon: sessionStore.getItem('promo_ribbon') || '',
    promo_id: sessionStore.getItem('promo_id') || '',
    percent_off: sessionStore.getItem('percent_off') || '',
    amount_off: sessionStore.getItem('amount_off') || '',
    cv_promo_ribbon: sessionStore.getItem('cv_promo_ribbon') || '',
    cv_promo_id: sessionStore.getItem('cv_promo_id') || '',
    cv_percent_off: sessionStore.getItem('cv_percent_off') || '',
    cv_amount_off: sessionStore.getItem('cv_amount_off') || '',
  };
};

/**
 * Store promo data in sessionStorage
 * @param {Array} promoData - Array of promo data from backend
 * @param {Array} cvPromoData - Array of CV promo data from backend
 * @param {string} affid - Affiliate ID used for fetching
 */
const storePromoDataInSession = (promoData, cvPromoData, affid = '', funnelId = '') => {
  const hasPromoData = promoData && promoData.length > 0;
  const hasCvPromoData = cvPromoData && cvPromoData.length > 0;
  
  if (hasPromoData || hasCvPromoData) {
    const promo = hasPromoData ? promoData[0] : {};
    const cvPromo = hasCvPromoData ? cvPromoData[0] : {};

    sessionStore.setItem('promo_ribbon', promo.promo_code || '');
    sessionStore.setItem('cv_promo_ribbon', cvPromo.promo_code || '');
    sessionStore.setItem('promo_id', promo.promo_id || '');
    sessionStore.setItem('cv_promo_id', cvPromo.promo_id || '');
    sessionStore.setItem('percent_off', promo.percent_off || '');
    sessionStore.setItem('cv_percent_off', cvPromo.percent_off || '');
    sessionStore.setItem('amount_off', promo.amount_off || '');
    sessionStore.setItem('cv_amount_off', cvPromo.amount_off || '');
    sessionStore.setItem('affid', affid); // Store the affiliate ID
    // Persist cache key so refresh without ?affid= does not wipe a valid promo
    sessionStore.setItem('promo_cache_key', buildCacheKey(affid, funnelId));

    logger.debug('✅ Promo data stored in sessionStorage:', {
      promo_code: promo.promo_code,
      cv_promo_code: cvPromo.promo_code,
      promo_id: promo.promo_id,
      cv_promo_id: cvPromo.promo_id,
      percent_off: promo.percent_off,
      cv_percent_off: cvPromo.percent_off,
      amount_off: promo.amount_off,
      cv_amount_off: cvPromo.amount_off,
      affid: affid,
      funnel: funnelId,
      promo_cache_key: buildCacheKey(affid, funnelId),
    });
  } else {
    logger.warn('⚠️ No promo data found to store in sessionStorage');
  }
};

/**
 * Fetch promo data from backend
 * @param {string|null} affid - Optional affiliate ID (will be extracted from URL if not provided)
 * @param {string|null} funnelId - Optional funnel id (e.g. variant-cv-1, variant-cv-nad). Derived from URL if omitted.
 * @returns {Promise<Object>} Promise resolving to promo data response
 */
export const fetchPromoData = async (affid = null, funnelId = null) => {
  try {
    // Extract affid from URL if not provided
    if (!affid) {
      const urlParams = new URLSearchParams(window.location.search);
      affid = urlParams.get('affid') || '';
    }

    // Derive funnel id from current path if not provided
    if (!funnelId) {
      funnelId = getFunnelIdFromPath(window.location.pathname);
    }

    // If no funnel found, skip promo fetch (invalid route, 404 will be shown)
    if (!funnelId) {
      logger.warn('⚠️ No valid funnel ID found for pathname, skipping promo fetch');
      return {
        success: false,
        message: 'Invalid funnel path',
      };
    }

    // Build the request body with affid + funnel
    const requestBody = new FormData();
    if (affid) {
      requestBody.append('affid', affid);
      logger.debug('📤 Sending affid with promo request:', affid);
    }
    requestBody.append('funnel', funnelId);
    logger.debug('📤 Sending funnel with promo request:', funnelId);

    // Forward oid from URL when present (only oid=23 is meaningful downstream)
    // const urlParams = new URLSearchParams(window.location.search);
    // const oid = urlParams.get('oid');
    // if (oid === '23') {
    //   requestBody.append('oid', oid);
    //   logger.debug('📤 Sending oid with promo request:', oid);
    // }

    const response = await fetch('/funnel-get-promo', {
      method: 'POST',
      credentials: 'include', // send cookies
      body: requestBody,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch promo data');
    }

    const result = await response.json();

    // logger.debug('🔍 Promo response:', result);

    if (result.success) {
      const { funnel, promo_text, cv_promo_text, promo_code, cv_promo_code, promo_data, cv_promo_data } = result.data;
      logger.debug('📣 Promo fetched:', { funnel, promo_text, cv_promo_text, promo_code, cv_promo_code, promo_data, cv_promo_data });

      // Store promo data in sessionStorage (including affid + funnel)
      storePromoDataInSession(promo_data, cv_promo_data, affid, funnel || funnelId);

      return {
        success: true,
        data: result.data,
      };
    } else {
      logger.warn('⚠️ Promo fetch unsuccessful:', result.data?.message);
      return {
        success: false,
        message: result.data?.message || 'Failed to fetch promo data',
      };
    }
  } catch (error) {
    logger.error('❌ Error fetching promo data:', error);
    return {
      success: false,
      message: error.message,
    };
  }
};

/**
 * Initialize promo data
 * Fetches promo data from backend if not already in sessionStorage
 * @param {string|null} affid - Optional affiliate ID (will be extracted from URL if not provided)
 * @param {string|null} funnelId - Optional funnel id; derived from URL if omitted.
 * @returns {Promise<Object>} Promise resolving to promo data
 */
export const initializePromoData = async (affid = null, funnelId = null) => {
  // Extract affid from URL if not provided; fall back to session so refresh
  // on /checkout/selection/:caseId (often without ?affid=) keeps the same promo.
  if (!affid) {
    const urlParams = new URLSearchParams(window.location.search);
    affid = urlParams.get('affid') || sessionStore.getItem('affid') || '';
  }

  // Derive funnel id from current path if not provided
  if (!funnelId) {
    funnelId = getFunnelIdFromPath(window.location.pathname);
  }

  // If no funnel found, skip promo init (invalid route, 404 will be shown)
  if (!funnelId) {
    logger.warn('⚠️ No valid funnel ID found for pathname, skipping promo initialization');
    return {
      success: false,
      message: 'Invalid funnel path',
    };
  }

  const currentCacheKey = buildCacheKey(affid, funnelId);
  let cachedKey = sessionStore.getItem('promo_cache_key') || '';

  // Legacy sessions stored promo fields but never wrote promo_cache_key.
  // Backfill instead of treating that as a cache miss (which cleared discounts on refresh).
  if (hasPromoDataInSession() && !cachedKey) {
    const sessionAffid = sessionStore.getItem('affid') || affid || '';
    cachedKey = buildCacheKey(sessionAffid, funnelId);
    sessionStore.setItem('promo_cache_key', cachedKey);
    logger.debug('🔧 Backfilled missing promo_cache_key:', cachedKey);
  }

  // Cache invalidation: clear when either affid or funnel changes.
  if (hasPromoDataInSession() && cachedKey !== currentCacheKey) {
    logger.debug('🔄 Promo cache key changed, clearing cached promo:', {
      cached: cachedKey,
      current: currentCacheKey,
    });
    clearPromoData();
    // Reset global flags to allow refetch
    globalPromoFetched = false;
    globalPromoFetchInProgress = false;
  }

  // CRITICAL: Triple-check to prevent duplicate fetches
  if (hasPromoDataInSession() || globalPromoFetched || globalPromoFetchInProgress) {
    const existingData = getPromoDataFromSession();
    logger.debug('ℹ️ Promo data already exists in session or fetch in progress, skipping:', {
      hasSession: hasPromoDataInSession(),
      fetched: globalPromoFetched,
      inProgress: globalPromoFetchInProgress,
      affid: affid,
      funnel: funnelId,
    });
    return {
      success: true,
      data: existingData,
      fromCache: true,
    };
  }

  // Set global flag to prevent simultaneous fetches
  globalPromoFetchInProgress = true

  try {
    // Fetch promo data from backend
    logger.debug('🔄 Fetching promo data from backend...', { affid, funnel: funnelId });
    const result = await fetchPromoData(affid, funnelId);

    // Mark as fetched
    globalPromoFetched = true
    globalPromoFetchInProgress = false

    return {
      ...result,
      fromCache: false,
    };
  } catch (error) {
    globalPromoFetchInProgress = false
    throw error
  }
};

/**
 * Clear promo data from sessionStorage
 */
export const clearPromoData = () => {
  sessionStore.removeItem('promo_ribbon');
  sessionStore.removeItem('cv_promo_ribbon');
  sessionStore.removeItem('promo_id');
  sessionStore.removeItem('cv_promo_id');
  sessionStore.removeItem('percent_off');
  sessionStore.removeItem('cv_percent_off');
  sessionStore.removeItem('amount_off');
  sessionStore.removeItem('cv_amount_off');
  sessionStore.removeItem('affid');
  sessionStore.removeItem('promo_cache_key');
  logger.debug('🗑️ Promo data cleared from sessionStorage');
};

